import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

export function ConseqraMark() {
  return <span className="brand-mark brand-mark--conseqra" aria-hidden="true">
    <svg viewBox="0 0 64 64" focusable="false">
      <path className="conseqra-flow conseqra-flow--one" d="M5 12 C20 12 20 28 31 31" />
      <path className="conseqra-flow conseqra-flow--two" d="M5 32 H28" />
      <path className="conseqra-flow conseqra-flow--three" d="M5 52 C20 52 20 36 31 33" />
      <path className="conseqra-flow conseqra-flow--out" d="M36 32 C47 32 47 20 57 20 M36 32 C47 32 47 44 57 44" />
      <circle className="conseqra-node" cx="5" cy="12" r="3" />
      <circle className="conseqra-node" cx="5" cy="32" r="3" />
      <circle className="conseqra-node" cx="5" cy="52" r="3" />
      <rect className="conseqra-decision" x="28" y="25" width="14" height="14" rx="3" transform="rotate(45 35 32)" />
      <circle className="conseqra-outcome" cx="58" cy="20" r="3.5" />
      <circle className="conseqra-outcome" cx="58" cy="44" r="3.5" />
    </svg>
  </span>
}

export function ConseqraLogo() {
  return <span className="brand-lockup brand-lockup--conseqra"><ConseqraMark /><span>CONSEQRA</span></span>
}

export function SemanticSecuredMark() {
  return <span className="brand-mark brand-mark--semantic" aria-hidden="true"><i /><i /><i /><b /></span>
}

export function SemanticSecuredLogo() {
  return <span className="brand-lockup brand-lockup--semantic"><SemanticSecuredMark /><span><strong>SEMANTIC</strong><strong>SECURED</strong></span></span>
}

export const AxiomMark = ConseqraMark
export const AxiomLogo = ConseqraLogo

const researchNav = [
  ['/research', 'Research overview'],
  ['/research/semantic-security/framework', 'Semantic Security Framework'],
  ['/research/semantic-security/framework/crosswalk', 'Framework crosswalk'],
] as const

export function SiteShell({ children }: { children: ReactNode }) {
  const location = useLocation()
  const mobileNavRef = useRef<HTMLDetailsElement | null>(null)

  useEffect(() => {
    const closeMenu = (event?: PointerEvent) => {
      const menu = mobileNavRef.current
      if (!menu) return
      if (!event || !menu.contains(event.target as Node)) menu.open = false
    }
    closeMenu()
    document.addEventListener('pointerdown', closeMenu)
    return () => document.removeEventListener('pointerdown', closeMenu)
  }, [location.pathname])

  const closeMobileMenu = () => {
    if (mobileNavRef.current) mobileNavRef.current.open = false
  }
  const pageTheme = location.pathname.startsWith('/conseqra') || location.pathname === '/platform'
    ? 'product'
    : location.pathname.startsWith('/research')
      ? 'research'
      : 'company'

  return (
    <div className={`site-shell site-shell--corporate site-shell--${pageTheme}`}>
      <style>{`
        .desktop-nav__group{position:relative;height:86px;display:flex;align-items:center}
        .desktop-nav__parent{display:inline-flex;align-items:center;gap:7px}
        .desktop-nav__parent::after{content:'⌄';color:#71869D;font-size:11px;transition:transform .18s ease}
        .desktop-nav__group:hover .desktop-nav__parent::after,.desktop-nav__group:focus-within .desktop-nav__parent::after{transform:rotate(180deg)}
        .desktop-nav__submenu{position:absolute;left:50%;top:68px;display:grid;width:220px;padding:10px;border:1px solid var(--axiom-border-subtle);border-radius:12px;background:rgba(11,22,40,.98);box-shadow:0 20px 50px rgba(0,0,0,.34);opacity:0;visibility:hidden;transform:translate(-50%,-6px);transition:opacity .16s ease,transform .16s ease,visibility .16s ease}
        .desktop-nav__group:hover .desktop-nav__submenu,.desktop-nav__group:focus-within .desktop-nav__submenu{opacity:1;visibility:visible;transform:translate(-50%,0)}
        .desktop-nav__submenu a{padding:11px 12px;border-radius:8px;font-size:13px}
        .desktop-nav__submenu a:hover,.desktop-nav__submenu a.active{background:#10233B}
        .mobile-nav__group{border-bottom:1px solid rgba(53,80,110,.45)}
        .mobile-nav__group summary{padding:12px;list-style:none;color:#D5DEE9;font-size:14px}
        .mobile-nav__group summary::after{content:'+';float:right;color:#7DD3FC}
        .mobile-nav__group[open] summary::after{content:'−'}
        .mobile-nav__submenu{display:grid;padding:0 0 8px 12px}
        .mobile-nav__submenu a{font-size:13px;color:#AFC0D2}
      `}</style>
      <div className="site-flow-background" aria-hidden="true">
        <svg viewBox="0 0 1600 1100" preserveAspectRatio="xMidYMid slice">
          <g className="site-flow-background__paths">
            <path d="M-120 160 C220 160 210 420 520 420 S860 250 1110 360 S1350 610 1740 610" />
            <path d="M-80 360 C250 360 300 560 560 560 S860 460 1080 520 S1370 820 1710 760" />
            <path d="M-140 760 C180 760 260 610 520 610 S870 760 1090 680 S1370 410 1720 470" />
            <path className="site-flow-background__authority" d="M180 0 C180 230 430 250 520 420 M270 1100 C270 830 440 790 560 560" />
          </g>
          <g className="site-flow-background__nodes">
            <circle cx="520" cy="420" r="8" /><circle cx="560" cy="560" r="8" />
            <rect x="1057" y="337" width="46" height="46" rx="8" transform="rotate(45 1080 360)" />
            <circle cx="1090" cy="680" r="9" /><circle cx="1370" cy="610" r="7" /><circle cx="1370" cy="820" r="7" />
          </g>
        </svg>
      </div>
      <header className="site-header">
        <NavLink to="/" className="brand-link" aria-label="Semantic Secured home">
          <SemanticSecuredLogo />
        </NavLink>
        <nav className="desktop-nav" aria-label="Primary">
          <div className="desktop-nav__group">
            <NavLink className={location.pathname === '/platform' ? 'desktop-nav__parent active' : 'desktop-nav__parent'} to="/conseqra">Conseqra</NavLink>
            <div className="desktop-nav__submenu">
              <NavLink to="/conseqra">Conseqra overview</NavLink>
              <NavLink to="/platform">Platform capabilities</NavLink>
            </div>
          </div>
          <div className="desktop-nav__group">
            <NavLink className={location.pathname.startsWith('/research') ? 'desktop-nav__parent active' : 'desktop-nav__parent'} to="/research">Research</NavLink>
            <div className="desktop-nav__submenu">
              {researchNav.map(([to, label]) => <NavLink key={to} to={to}>{label}</NavLink>)}
            </div>
          </div>
        </nav>
        <NavLink className="button button--small" to="/contact">Talk to us</NavLink>
        <details ref={mobileNavRef} className="mobile-nav">
          <summary>Menu</summary>
          <div className="mobile-nav__panel">
            <details className="mobile-nav__group" open={location.pathname === '/platform'}>
              <summary>Conseqra</summary>
              <div className="mobile-nav__submenu">
                <NavLink to="/conseqra" onClick={closeMobileMenu}>Conseqra overview</NavLink>
                <NavLink to="/platform" onClick={closeMobileMenu}>Platform capabilities</NavLink>
              </div>
            </details>
            <details className="mobile-nav__group" open={location.pathname.startsWith('/research')}>
              <summary>Research</summary>
              <div className="mobile-nav__submenu">
                {researchNav.map(([to, label]) => <NavLink key={to} to={to} onClick={closeMobileMenu}>{label}</NavLink>)}
              </div>
            </details>
            <NavLink to="/contact" onClick={closeMobileMenu}>Contact</NavLink>
          </div>
        </details>
      </header>
      <main key={location.pathname}>{children}</main>
      <footer className="site-footer">
        <SemanticSecuredLogo />
        <div className="footer-links">
          <NavLink to="/conseqra">Conseqra</NavLink>
          <NavLink to="/research">Research</NavLink>
          <NavLink to="/research/semantic-security/framework">Framework</NavLink>
        </div>
        <span>© 2026 Semantic Secured</span>
      </footer>
    </div>
  )
}

export function Eyebrow({ children, proof = false }: { children: ReactNode; proof?: boolean }) {
  return <div className={proof ? 'eyebrow eyebrow--proof' : 'eyebrow'}>{children}</div>
}

export function ButtonLink({ to, children, secondary = false }: { to: string; children: ReactNode; secondary?: boolean }) {
  return <NavLink className={secondary ? 'button button--secondary' : 'button'} to={to}>{children}</NavLink>
}

export function Card({ title, children, proof = false, light = false }: { title: string; children: ReactNode; proof?: boolean; light?: boolean }) {
  return <article className={`card${light ? ' card--light' : ''}`}><span className={proof ? 'accent accent--proof' : 'accent'} /><h3>{title}</h3><div className="muted">{children}</div></article>
}

export function PageHero({ eyebrow, title, body }: { eyebrow: string; title: string; body: string }) {
  return <section className="page-hero"><Eyebrow>{eyebrow}</Eyebrow><h1>{title}</h1><p className="lede">{body}</p></section>
}

export function DecisionTrace({ compact = false }: { compact?: boolean }) {
  const steps = ['Intent','Context','Evidence','Authority','Decision','Binding','Outcome']
  return <div className={`decision-trace${compact ? ' decision-trace--compact' : ''}`}>
    <Eyebrow>Decision trace</Eyebrow>
    <div className="decision-trace__steps">{steps.map((s, i) => <div className={`trace-step${i === 4 ? ' trace-step--active' : ''}`} key={s}><span /><b>{s}</b></div>)}</div>
    {!compact && <div className="authorised-pill">Authorised</div>}
  </div>
}

export function Lifecycle({ research = false, detailed = false }: { research?: boolean; detailed?: boolean }) {
  const steps = research
    ? [
        ['Intent', 'Define the purpose and consequence boundary.'],
        ['Context', 'Assemble the signals and entities that give the decision meaning.'],
        ['Provenance', 'Preserve where information came from and how it changed.'],
        ['Evidence', 'Verify the facts supporting the reasoning.'],
        ['Reasoning', 'Produce an inspectable conclusion from the available evidence.'],
        ['Decision', 'Record the conclusion, confidence and accountable owner.'],
        ['Action', 'Bind the decision to a permitted execution.'],
        ['Memory', 'Retain the outcome without turning history into unquestioned truth.'],
      ]
    : [
        ['Intent', 'Define the objective, accountable owner, permitted scope and measurable outcome before reasoning begins. Conseqra preserves that original mandate as the decision’s governing reference point.'],
        ['Context', 'Assemble live operational signals, affected systems, identities and environmental conditions. Each input remains attributable so the decision can be reconstructed against the circumstances that shaped it.'],
        ['Proposal', 'Generate a reasoned course of action with stated confidence, alternatives and supporting evidence. The proposal remains advisory until governance and authority checks have independently passed.'],
        ['Evaluation', 'Test the proposal against policy, risk appetite, consequence thresholds and conflicting evidence. Failed or uncertain conditions become explicit exceptions rather than disappearing inside model reasoning.'],
        ['Authorise', 'Resolve who or what holds delegated authority for this exact consequence and scope. Escalation is introduced only when the available authority envelope cannot legitimately permit the action.'],
        ['Action Binding', 'Convert the authorised decision into a tamper-evident instruction with fixed targets, parameters, permissions, expiry and revocation controls. Execution cannot silently exceed what was approved.'],
        ['Execution', 'Carry out the bounded instruction through controlled integrations while recording every material state change. Drift, failure or changed conditions can pause, revoke or redirect execution.'],
        ['Outcome', 'Verify the real-world result against the intended outcome, capture impact and preserve the complete evidence chain. Learning is retained without rewriting the authority or context of the original decision.'],
      ]
  const [activeStep, setActiveStep] = useState(4)

  if (detailed) {
    const [activeLabel, activeDescription] = steps[activeStep]
    return <div className="governed-path">
      <div className="governed-path__rail">
        {steps.map(([label], index) => <button
          type="button"
          className={index === activeStep ? 'governed-path__step is-active' : 'governed-path__step'}
          key={label}
          onMouseEnter={() => setActiveStep(index)}
          onFocus={() => setActiveStep(index)}
          onClick={() => setActiveStep(index)}
          aria-pressed={index === activeStep}
        >
          <span className="governed-path__number">{String(index + 1).padStart(2, '0')}</span>
          <i aria-hidden="true" />
          <strong>{label}</strong>
        </button>)}
      </div>
      <div className="governed-path__inspector" aria-live="polite">
        <span>Stage {String(activeStep + 1).padStart(2, '0')}</span>
        <h3>{activeLabel}</h3>
        <p>{activeDescription}</p>
      </div>
    </div>
  }

  return <div className="lifecycle">{steps.map(([label], i) => <div className={i === 4 || i === 5 ? 'lifecycle__step lifecycle__step--active' : 'lifecycle__step'} key={label}><span /><small>{label}</small></div>)}</div>
}
